import { useState, useEffect, useRef, useCallback, useMemo, ChangeEvent } from 'react';
import moment, { Moment } from 'moment';
import classNames from 'classnames';
import {
  TextInput,
  TextArea,
  Radio,
  Dropdown,
  Button,
  DatePicker,
  Spinner,
  Icon,
} from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import styles from './ScoutingForm.css';
import {
  requestDeleteActivity,
  reqNewActivity,
  setSelectedActivity,
} from '../../../../actions/scoutingActions';
import { getCurrentSeasonYear } from '../../../../selectors/seasonSelectors';
import { getApiUrl } from '../../../../utils/url';
import { getActivityColors } from '../../../../utils/color';
import {
  ASSET_ACCEPTED_TYPES,
} from '../../../../constants/scouting';
import ConfirmationModal from '../../../Modal/ConfirmationModal';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { closeModal } from '../../../../actions/modalActions';
import { trackHeapEvent } from '../../../../actions/analyticsActions';
import { setPlacingPin } from '../../../../actions/mapActions';
import { Activity, ActivityColor, EnhancedActivity, ModalMode, ShapeType } from '../../../../types';
import { useAppDispatch, useAppSelector } from '../../../../utils';

type ScoutingFormState = {
  title: string,
  note: string,
  permanent: boolean,
  color?: ActivityColor,
  date: string,
  assetLoaded: boolean,
  file: File | null,
  incorrectFileFormat: boolean,
};

type ScoutingFormInitialValues = Pick<ScoutingFormState, 'title' | 'note' | 'permanent' | 'color' | 'date'>;

type ScoutingFormProps = {
  index: number;
}

const getInitialValues = (activity?: Activity): ScoutingFormInitialValues => ({
  title: activity?.title ?? '',
  note:
    activity?.comments && activity.comments.length > 0 && activity.comments
      ? activity.comments[0].note
      : '',
  permanent: activity?.permanent ?? false,
  color: activity?.color ?? ActivityColor.BLUE,
  date: activity?.scoutedAt ?? '',
});

export const ScoutingForm = ({ index }: ScoutingFormProps) => {
  const dispatch = useAppDispatch();
  const wrapperRef = useRef(null);
  const [confirmationModal, setConfirmationModal] = useState(false);

  const currentSeasonYear = useAppSelector(state => getCurrentSeasonYear(state, { index }));
  const isLoadingActivity = useAppSelector(state => state.scouting.isLoadingActivity);
  
  // NOTE: For now meta.edit is used a view only mode so it disables the fields
  // but in the future it will be used as an actual edit mode
  const {
    activity: untypedActivity,
    mode,
    lng: metaLng,
    lat: metaLat,
    edit: viewOnlyMode = false
  } = useAppSelector(state => state.modal.meta);
  const activity = untypedActivity as Activity | EnhancedActivity;

  const field = useAppSelector(state => state.field);

  const initialValues = useMemo(() => getInitialValues(activity), [activity]);

  const [state, setState] = useState<ScoutingFormState>({
    ...initialValues,
    date:
      initialValues.date ||
      moment()
        .set('year', parseInt(currentSeasonYear, 10))
        .format('YYYY-MM-DD'),
    assetLoaded: false,
    file: null,
    incorrectFileFormat: false,
  });

  const isShowingPin = useMemo(() => mode === ModalMode.SHOW_PIN, [mode]);
  const isAddingPin = mode === ModalMode.ADD_PIN;
  const isAddingNote = mode === ModalMode.ADD_NOTE;
  const isCreatingActivity = (isAddingPin || isAddingNote);

  useEffect(() => {
    if (isShowingPin) {
      dispatch(trackHeapEvent('View Scouting Pin'));
    }
  }, [dispatch, isShowingPin]);

  const handleClickClose = useCallback(() => {
    setConfirmationModal(false);
    dispatch(closeModal(index));
    dispatch(setPlacingPin(false));
  }, [index, dispatch]);

  const handleClickGoBack = useCallback(() => {
    setConfirmationModal(false);
  }, []);

  const onChangeFile = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];

    if (file) {
      setState((oldState) => ({
        ...oldState,
        file,
        incorrectFileFormat: !ASSET_ACCEPTED_TYPES.includes(file.type),
      }));
    }
  }, []);

  const setAssetStatus = useCallback(() => {
    setState((oldState) => ({ ...oldState, assetLoaded: !oldState.assetLoaded }));
  }, []);

  const onSubmitFieldNote = useCallback(() => {
    const payload = {
      title: state.title,
      comments: [{ note: state.note }],
      color: state.color,
      field: field.uuid,
      permanent: state.permanent,
      scoutedAt: moment(state.date).toISOString(),
      index,
    };

    dispatch(reqNewActivity(payload, state.file));
    dispatch(trackHeapEvent('Create Field Note'));
  }, [dispatch, index, state, field]);

  const onSubmitPin = useCallback(() => {
    const payload = {
      title: state.title,
      comments: [{ note: state.note }],
      color: state.color?.toLowerCase(),
      field: field.uuid,
      permanent: state.permanent,
      scoutedAt: moment(state.date).toISOString(),
      location: {
        type: ShapeType.POINT,
        coordinates: [metaLng, metaLat],
      },
      index,
    };

    dispatch(reqNewActivity(payload, state.file));
    dispatch(trackHeapEvent('Create Scouting Pin'));
  }, [dispatch, index, state, metaLng, metaLat, field]);

  const onCloseModal = useCallback(() => {
    dispatch(closeModal(index));
    dispatch(setSelectedActivity(index, null));
  }, [index, dispatch]);

  const deleteActivity = useCallback(() => {
    dispatch(requestDeleteActivity(activity, { index }));
  }, [index, activity, dispatch]);

  useClickOutside(wrapperRef, () =>
    !viewOnlyMode ? setConfirmationModal(true) : onCloseModal()
  );

  const showAsset = viewOnlyMode && activity && activity.assets[0];

  const shouldShowCoordinates = isShowingPin || isAddingPin;

  const existingLat =
    activity &&
    activity.location &&
    activity.location.coordinates[1];

  const existingLng =
    activity &&
    activity.location &&
    activity.location.coordinates[0];

  const lat = isAddingPin ? metaLat : existingLat;
  const lng = isAddingPin ? metaLng : existingLng;

  const assetClass = classNames(styles.asset, {
    [styles.hiddenAsset]: !state.assetLoaded,
  });

  // NOTE: textarea needs the scrollbar thats why we are using readOnly and adding
  // "disabled" like CSS
  const textAreaClass = classNames(styles.pinNoteTextArea, {
    [styles.pinNoteTextAreaDisabled]: viewOnlyMode,
  });

  if (confirmationModal) {
    return (
      <ConfirmationModal
        className={styles.scoutingForm}
        confirmButtonText={getMessage('confirmationModal.close')}
        handleClickConfirm={handleClickClose}
        handleClickGoBack={handleClickGoBack}
        handleClickCancel={handleClickClose}
        title={getMessage('confirmationModal.title')}
      />
    );
  }

  return (
    <div ref={wrapperRef} className={styles.scoutingForm} data-test-id="scouting-form">
        <div className={styles.pinModalHeader}>{getMessage('scouting.form')}</div>
        <TextInput
          testId="scouting-form-field-title"
          disabled={viewOnlyMode}
          label={getMessage('scouting.form.title')}
          maxLength="50"
          name="title"
          onChange={({ target: { value } }) => setState(prevState => ({ ...prevState, title: value }))}
          placeholder={getMessage('scouting.form.addTitle')}
          value={state.title}
        />
        <div className={styles.pinModalNotesHeader}>
          {getMessage('scouting.form.note')}
        </div>
        <TextArea
          className={textAreaClass}
          data-test-id="scouting-form-field-note"
          label={getMessage('scouting.form.note')}
          maxLength="4000"
          name="note"
          onChange={({ target: { value } }) => setState(prevState => ({ ...prevState, note: value }))}
          placeholder={getMessage('scouting.form.addNote')}
          readOnly={viewOnlyMode}
          value={state.note}
        />
        <Dropdown
          className={styles.pinColorDropdown}
          data-test-id="scouting-form-dropdown-colors"
          disabled={viewOnlyMode}
          label={
            <div className={styles.pinColorDropdownLabel}>
              {getMessage('scouting.form.color')}
              <div
                className={styles.pinColorDropdownPreview}
                style={{ backgroundColor: state.color }}
              />
            </div>
          }
          options={getActivityColors()}
          selectedId={state.color}
          setValue={(_value, id: ActivityColor) => setState(prevState => ({ ...prevState, color: id }))}
        />
        <DatePicker
          data-test-id="scouting-form-datepicker"
          disabled={viewOnlyMode}
          label={getMessage('scouting.form.date')}
          maxDate={`12/31/${currentSeasonYear}`}
          minDate={`01/01/${currentSeasonYear}`}
          moment={moment}
          onChangeDate={(date: Moment | null) => {
            if (!date) {
              return;
            }
            setState(prevState => ({ ...prevState, date: date.format('YYYY-MM-DD') }));
          }}
          selected={state.date}
        />
        <div className={styles.pinRadioContainer}>
          <Radio
            checked={!state.permanent}
            data-test-id="scouting-form-radio-seasonal"
            disabled={viewOnlyMode}
            label={getMessage('scouting.form.seasonal')}
            onChange={() => setState(prevState => ({ ...prevState, permanent: false }))}
          />
          <Radio
            checked={state.permanent}
            data-test-id="scouting-form-radio-permanent"
            disabled={viewOnlyMode}
            label={getMessage('scouting.form.permanent')}
            onChange={() => setState(prevState => ({ ...prevState, permanent: true }))}
          />
        </div>
        {shouldShowCoordinates && (
          <div className={styles.pinModalLocationContainer}>
            <div className={styles.pinModalLocation}>
              {getMessage('scouting.form.latitude')}
              <div>{lat}</div>
            </div>
            <div className={styles.pinModalLocation}>
              {getMessage('scouting.form.longitude')}
              <div>{lng}</div>
            </div>
          </div>
        )}
        {isCreatingActivity && (
          <div className={styles.inputFileContainer}>
            <input
              id="file-input"
              accept={ASSET_ACCEPTED_TYPES.join(', ')}
              className={styles.inputFile}
              data-test-id="scouting-form-file-input"
              onChange={onChangeFile}
              type="file"
            />
            <label
              htmlFor="file-input"
              className={styles.inputFileLabel}
              data-test-id="scouting-form-file-label"
            >
              <Icon 
                className={styles.inputFileLabelIcon}
                glyph={Icon.set.CAMERA}
                testId="scouting-form-camera-icon"
              />
              {state.file
                ? state.file.name
                : getMessage('enhancedScouting.form.addPhoto')}
            </label>
            {state.incorrectFileFormat && (
              <div
                className={styles.inputFileError}
                data-test-id="scouting-form-file-input-error"
              >
                {getMessage('scouting.form.incorrectFileFormat')}
              </div>
            )}
          </div>
        )}
        {showAsset && (
          <div className={styles.assetContainer}>
            {!state.assetLoaded && (
              <Spinner data-test-id="scouting-form-asset-spinner" />
            )}
            <img
              alt={getMessage('image.scouting.form')}
              className={assetClass}
              data-test-id="scouting-form-asset-img"
              onLoad={setAssetStatus}
              src={getApiUrl(`/api/tenderfoot/${activity?.assets[0].uri}`)}
            />
          </div>
        )}
        <div className={styles.submitBtnContainer}>
          <Button
            data-test-id="scouting-form-delete"
            disabled={!viewOnlyMode}
            isLoading={isLoadingActivity}
            mode="negative"
            onClick={deleteActivity}
          >
            {getMessage('scouting.form.delete')}
          </Button>
          <Button
            data-test-id="scouting-form-submit"
            disabled={viewOnlyMode || state.incorrectFileFormat}
            isLoading={isLoadingActivity}
            mode="primary"
            onClick={
              isAddingNote
                ? onSubmitFieldNote
                : onSubmitPin
            }
          >
            {getMessage('scouting.form.submit')}
          </Button>
        </div>
      </div>
  );
};
